import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Grid,
  Divider,
  TextField,
  Box,
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SearchIcon from '@mui/icons-material/Search';
import { AppDialog } from '../ui';

type TagPickerPanelProps = {
  tags: any[];
  categories: any[];
  selectedTagIds: number[];
  onChange: (tagIds: number[]) => void;
  disabled?: boolean;
  maxHeight?: number | string;
};

export function TagPickerPanel({
  tags,
  categories,
  selectedTagIds,
  onChange,
  disabled = false,
  maxHeight = '55vh',
}: TagPickerPanelProps) {
  const [search, setSearch] = useState('');
  const checkedTagIds = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);

  const categoryNameById = useMemo(() => {
    const map: Record<number, string> = {};
    (categories || []).forEach((cat: any) => {
      map[cat.id] = cat.name;
    });
    return map;
  }, [categories]);

  const sortedGroupedTags = useMemo(() => {
    const query = search.trim().toLowerCase();
    const activeTags = (tags || []).filter((tag: any) => !tag.isDeleted);
    const filtered = query
      ? activeTags.filter((tag: any) => (tag.name || '').toLowerCase().includes(query))
      : activeTags;

    const grouped: Record<string, any[]> = {};
    (categories || [])
      .filter((cat: any) => !cat.isDeleted)
      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((cat: any) => {
        grouped[cat.name] = [];
      });

    filtered.forEach((tag: any) => {
      const categoryName = categoryNameById[tag.categoryId] || tag.categoryName || 'Uncategorized';
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(tag);
    });

    return Object.entries(grouped).filter(([, list]) => (list as any[]).length > 0) as [string, any[]][];
  }, [tags, categories, categoryNameById, search]);

  const handleCheckboxChange = (tagId: number) => {
    if (disabled) return;
    const next = new Set(checkedTagIds);
    if (next.has(tagId)) next.delete(tagId);
    else next.add(tagId);
    onChange(Array.from(next));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search tags..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        disabled={disabled}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 1, maxHeight }}>
        {sortedGroupedTags.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No tags found
          </Typography>
        )}

        {sortedGroupedTags.map(([category, tagList], index, arr) => (
          <Box key={category} sx={{ mb: 2 }}>
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 0.5 }}
            >
              {category}
            </Typography>
            <Grid container spacing={0.5}>
              {tagList.map((tag: any) => (
                <Grid item xs={6} sm={4} md={3} key={tag.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={checkedTagIds.has(tag.id)}
                        onChange={() => handleCheckboxChange(tag.id)}
                        disabled={disabled}
                      />
                    }
                    label={tag.name}
                    sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                  />
                </Grid>
              ))}
            </Grid>
            {index < arr.length - 1 && <Divider sx={{ mt: 1.5, mb: 1 }} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

type TagPickerPopupProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  tags: any[];
  categories: any[];
  selectedTagIds: number[];
  onConfirm: (tagIds: number[]) => void | Promise<void>;
  confirmLabel?: string;
  loading?: boolean;
};

export default function TagPickerPopup({
  open,
  onClose,
  title = 'Select Tags',
  tags,
  categories,
  selectedTagIds,
  onConfirm,
  confirmLabel = 'Save',
  loading = false,
}: TagPickerPopupProps) {
  const [checkedTagIds, setCheckedTagIds] = useState<number[]>([]);

  useEffect(() => {
    if (open) {
      setCheckedTagIds([...selectedTagIds]);
    }
  }, [open, selectedTagIds]);

  const isChanged = useMemo(() => {
    if (checkedTagIds.length !== selectedTagIds.length) return true;
    const initial = new Set(selectedTagIds);
    return checkedTagIds.some((id) => !initial.has(id));
  }, [checkedTagIds, selectedTagIds]);

  const handleConfirm = async () => {
    await onConfirm(checkedTagIds);
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={title}
      pending={loading}
      maxWidth="md"
      actions={(
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
            Selected: {checkedTagIds.length}
          </Typography>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <LoadingButton variant="contained" onClick={handleConfirm} loading={loading} disabled={!isChanged || loading}>
            {confirmLabel}
          </LoadingButton>
        </>
      )}
    >
      <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column' }}>
        <TagPickerPanel
          tags={tags}
          categories={categories}
          selectedTagIds={checkedTagIds}
          onChange={setCheckedTagIds}
          disabled={loading}
        />
      </Box>
    </AppDialog>
  );
}
